import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { supabase } from "../config";
import ShareCard from "./ShareCard";
import { donorBlocks, dedicationPhrase, treesWord, PRICE_LINE } from "../shareCard";
import { resolveTeam, isGroupForest, forestLabel, forestUrl, forestPath } from "../forests";

const PAID_STATUS_OPTIONS = [
  { label: "Zelle Sent in Dollars", value: "zelle_paid" },
  { label: "GPay Sent in Rupees", value: "gpay_paid" },
  { label: "Pledging NOW will pay later", value: "pledged" },
];

function ContributeForm() {
  // /contribute?team=<id or short link, e.g. recw> (from a forest page or a post) picks that team.
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get("team");
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    whatsapp: "",
    email: "",
    num_trees: "",
    dedicated_to: "",
    payment_status: "",
    team: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    async function fetchTeams() {
      const { data } = await supabase
        .from("sritaru_teams")
        .select("team_id, team_name")
        .order("team_name");
      if (data) {
        setTeams(data);
        const picked = resolveTeam(teamParam, data);
        if (picked) setForm((f) => ({ ...f, team: f.team || picked }));
      }
    }
    fetchTeams();
  }, [teamParam]);

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required";
    if (!form.last_name.trim()) errs.last_name = "Last name is required";
    if (!form.whatsapp) errs.whatsapp = "WhatsApp number is required";
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Enter a valid email address";
    }
    if (!form.num_trees || parseInt(form.num_trees) <= 0)
      errs.num_trees = "Number of trees must be greater than 0";
    if (!form.dedicated_to.trim())
      errs.dedicated_to = "Dedication field is required";
    if (!form.payment_status) errs.payment_status = "Select a payment status";
    if (!form.team) errs.team = "Select a forest or team";
    return errs;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("sritaru_contributions").insert([
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          whatsapp: form.whatsapp,
          email: form.email.trim().toLowerCase(),
          num_trees: parseInt(form.num_trees),
          dedicated_to: form.dedicated_to.trim(),
          payment_status: form.payment_status,
          team_id: form.team ? parseInt(form.team) : null,
        },
      ]);

      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const numTrees = parseInt(form.num_trees);
    const teamName = teams.find((t) => String(t.team_id) === String(form.team))?.team_name;
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg mx-auto text-center">
        <div className="text-5xl mb-4">&#127795;</div>
        <h2 className="text-2xl font-bold text-sritaru-700 mb-2">
          Thank You!
        </h2>
        <p className="text-gray-700 mb-4">
          You have contributed{" "}
          <span className="font-bold text-sritaru-800">
            {parseInt(form.num_trees).toLocaleString()}
          </span>{" "}
          tree{parseInt(form.num_trees) > 1 ? "s" : ""} dedicated to{" "}
          <span className="font-semibold">{form.dedicated_to}</span>.
        </p>
        <ShareCard
          title="📣 Share your trees, bring a friend"
          blocks={donorBlocks({
            trees: numTrees,
            dedication: form.dedicated_to,
            pledged: form.payment_status === "pledged",
            forestName: teamName,
          })}
          shareText={`I ${form.payment_status === "pledged" ? "pledged" : "planted"} ${numTrees.toLocaleString("en-US")} fruit ${treesWord(numTrees)} ${dedicationPhrase(form.dedicated_to)} 🌱 with SriTaru Sankalpam, on farmers' land in AP & Telangana. ${PRICE_LINE}. Your turn:`}
          shareUrl={teamName ? forestUrl({ id: form.team, name: teamName }) : "https://sritaru.kathakala.us"}
          fileName={`sritaru-${numTrees}-trees`}
        />
        <div className="bg-sritaru-50 rounded-lg p-4 mt-4 text-left">
          <p className="font-semibold text-sritaru-800 mb-1">&#9997;&#65039; Write a letter to your tree</p>
          <p className="text-sm text-gray-700 mb-3">
            In your own handwriting, to your tree or to the person you dedicated it to.
            With your permission, we will share some letters in our reels and posts.
          </p>
          <Link
            to="/letters"
            state={{
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              dedicated_to: form.dedicated_to.trim(),
            }}
            className="inline-block bg-sritaru-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-sritaru-700 transition-colors"
          >
            Send a letter
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link
            to="/wall"
            className="bg-sritaru-600 text-white px-6 py-2 rounded-lg hover:bg-sritaru-700 transition-colors"
          >
            View Contributor Wall
          </Link>
          {form.team && (
            <Link
              to={forestPath({ id: form.team, name: teamName })}
              className="bg-sritaru-600 text-white px-6 py-2 rounded-lg hover:bg-sritaru-700 transition-colors"
            >
              See your forest
            </Link>
          )}
          <button
            onClick={() => {
              setSubmitted(false);
              setForm({
                first_name: "",
                last_name: "",
                whatsapp: "",
                email: "",
                num_trees: "",
                dedicated_to: "",
                payment_status: "",
                team: "",
              });
            }}
            className="bg-earth-500 text-white px-6 py-2 rounded-lg hover:bg-earth-600 transition-colors"
          >
            Contribute More
          </button>
        </div>
      </div>
    );
  }

  const selectedTeam = teams.find((t) => String(t.team_id) === String(form.team))?.team_name;
  const inputClass =
    "w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-sritaru-400 focus:border-sritaru-400 outline-none transition-colors";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const errorClass = "text-red-500 text-xs mt-1";

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-lg mx-auto space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>First Name *</label>
          <input
            type="text"
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            className={inputClass}
          />
          {errors.first_name && (
            <p className={errorClass}>{errors.first_name}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>Last Name *</label>
          <input
            type="text"
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            className={inputClass}
          />
          {errors.last_name && (
            <p className={errorClass}>{errors.last_name}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>WhatsApp Number *</label>
        <PhoneInput
          international
          defaultCountry="US"
          value={form.whatsapp}
          onChange={(val) => {
            setForm({ ...form, whatsapp: val || "" });
            if (errors.whatsapp) setErrors({ ...errors, whatsapp: "" });
          }}
          className={inputClass}
        />
        {errors.whatsapp && <p className={errorClass}>{errors.whatsapp}</p>}
      </div>

      <div>
        <label className={labelClass}>Email *</label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          className={inputClass}
        />
        {errors.email && <p className={errorClass}>{errors.email}</p>}
      </div>

      <div>
        <label className={labelClass}>Number of Trees *</label>
        <input
          type="number"
          name="num_trees"
          min="1"
          value={form.num_trees}
          onChange={handleChange}
          placeholder="e.g. 10"
          className={inputClass}
        />
        {errors.num_trees && <p className={errorClass}>{errors.num_trees}</p>}
      </div>

      <div>
        <label className={labelClass}>Dedicated To *</label>
        <input
          type="text"
          name="dedicated_to"
          value={form.dedicated_to}
          onChange={handleChange}
          placeholder="In memory of / In honor of..."
          className={inputClass}
        />
        {errors.dedicated_to && (
          <p className={errorClass}>{errors.dedicated_to}</p>
        )}
      </div>

      <div>
        <label className={labelClass}>Payment Status *</label>
        <select
          name="payment_status"
          value={form.payment_status}
          onChange={handleChange}
          className={inputClass}
        >
          <option value="">-- Select --</option>
          {PAID_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.payment_status && (
          <p className={errorClass}>{errors.payment_status}</p>
        )}
      </div>

      <div>
        <label className={labelClass}>Forest or Team *</label>
        <select
          name="team"
          value={form.team}
          onChange={handleChange}
          className={inputClass}
        >
          <option value="">-- Select Forest or Team --</option>
          {[
            ["Group forests", teams.filter((t) => isGroupForest(t.team_name))],
            ["Teams", teams.filter((t) => !isGroupForest(t.team_name))],
          ].map(([label, list]) =>
            list.length ? (
              <optgroup key={label} label={label}>
                {list.map((t) => (
                  <option key={t.team_id} value={t.team_id}>
                    {t.team_name}
                  </option>
                ))}
              </optgroup>
            ) : null
          )}
        </select>
        {selectedTeam && isGroupForest(selectedTeam) && (
          <p className="text-xs text-sritaru-700 mt-1">
            &#127795; Your trees grow in the {forestLabel(selectedTeam)}.
          </p>
        )}
        {errors.team && <p className={errorClass}>{errors.team}</p>}
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-sritaru-600 text-white font-semibold py-3 rounded-lg hover:bg-sritaru-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "Submitting..." : "Contribute Trees"}
      </button>
    </form>
  );
}

export default ContributeForm;
